import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Navbar, Nav } from 'react-bootstrap';
import { LayoutList, Columns, ListChecks, Globe, Star } from 'lucide-react';
import withAuth from '../../../HOCs/withAuth';
import {
  BucketListProvider,
  useBucketList
} from '../../contexts/bucket-list-context';
import {
  IBucketListItem,
  BucketListRegion
} from '../../../Models/bucket-list/bucket-list.model';
import BucketListRegionGroup from './components/bucket-list-region-group';
import AddToBucketListModal from './components/add-to-bucket-list-modal';
import tripsService from '../../../services/trips/trips.service';
import settingsService from '../../../services/settings.service';
import ThSearchForm from '../../forms/th-search-form';
import Button from '../../UI/button';
import './bucket-list-page.scss';

const REGIONS = Object.values(BucketListRegion);

function BucketListPage() {
  const { byRegion, isLoading, error, bucketList, addItemToTrip, removeItem } =
    useBucketList();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState<IBucketListItem | null>(null);
  const [groupLayout, setGroupLayout] = useState<'vertical' | 'horizontal'>(
    'vertical'
  );

  // ── Add-to-trip state ──────────────────────────────────────────────────────
  const [addToTripItem, setAddToTripItem] = useState<IBucketListItem | null>(
    null
  );
  const [trips, setTrips] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [tripModalLoading, setTripModalLoading] = useState(false);
  const [tripModalError, setTripModalError] = useState<string | null>(null);
  const [addingToTrip, setAddingToTrip] = useState(false);

  const totalCount = bucketList?.items?.length ?? 0;

  // ── Load persisted layout preference ──────────────────────────────────────
  useEffect(() => {
    settingsService
      .getUIPreferences()
      .then((prefs) => {
        if (prefs.bucketListLayout) {
          setGroupLayout(prefs.bucketListLayout);
        }
      })
      .catch(() => {
        // best-effort
      });
  }, []);

  // ── Client-side search + region filter ──────────────────────────────────────
  const filteredByRegion = useMemo(() => {
    let groups = byRegion;

    if (selectedRegion) {
      groups = groups.filter((g) => g.region === selectedRegion);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      groups = groups
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (item) =>
              item.title.toLowerCase().includes(q) ||
              item.description?.toLowerCase().includes(q) ||
              item.notes?.toLowerCase().includes(q) ||
              item.tags?.some((t) => t.toLowerCase().includes(q))
          )
        }))
        .filter((g) => g.items.length > 0);
    }

    return groups;
  }, [byRegion, searchQuery, selectedRegion]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleLayoutToggle = async () => {
    const next = groupLayout === 'vertical' ? 'horizontal' : 'vertical';
    setGroupLayout(next);
    try {
      await settingsService.updateUIPreferences({ bucketListLayout: next });
    } catch {
      // best-effort
    }
  };

  const handleEdit = (item: IBucketListItem) => {
    setEditItem(item);
    setShowAddModal(true);
  };

  const handleRemove = async (itemId: string) => {
    try {
      await removeItem(itemId);
    } catch {
      // Error logged in service
    }
  };

  const handleAddToTrip = async (item: IBucketListItem) => {
    setAddToTripItem(item);
    setSelectedTripId('');
    setTripModalError(null);
    setTripModalLoading(true);
    try {
      const allTrips = await tripsService.getAllTrips(200);
      setTrips(
        allTrips
          .filter((t) => !t.deleted)
          .map((t) => ({ id: String(t.id), title: t.title }))
      );
    } catch {
      setTripModalError('Failed to load trips');
    } finally {
      setTripModalLoading(false);
    }
  };

  const handleConfirmAddToTrip = async () => {
    if (!addToTripItem || !selectedTripId) return;
    setAddingToTrip(true);
    setTripModalError(null);
    try {
      await addItemToTrip(addToTripItem.id, selectedTripId);
      setAddToTripItem(null);
    } catch (err: any) {
      setTripModalError(err?.message || 'Failed to add to trip');
    } finally {
      setAddingToTrip(false);
    }
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setEditItem(null);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <>
      <div
        className='gradient-bg-login'
        style={{
          right: '-200px',
          top: '-300px',
          width: '100%',
          height: '900px',
          transform: 'rotate(45deg)'
        }}
      />
      <div
        className='overflow-y-auto'
        style={{
          height: 'calc(100vh - var(--header-height))',
          paddingBottom: '20px'
        }}
      >
        <div className='container-px'>
          {/* Trips-style navbar: search left · title center · actions right */}
          <Navbar
            className='top-navbar-page w-100 justify-content-between p-0'
            style={{ margin: '32px 0 24px' }}
          >
            <Nav className='d-flex align-items-center gap-3'>
              <button
                className='btn bl-layout-toggle'
                onClick={handleLayoutToggle}
                title={
                  groupLayout === 'vertical'
                    ? 'Switch to horizontal layout'
                    : 'Switch to vertical layout'
                }
              >
                {groupLayout === 'vertical' ? (
                  <Columns size={24} />
                ) : (
                  <LayoutList size={24} />
                )}
              </button>
              <ThSearchForm
                handleSubmit={handleSearchSubmit}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder='Search bucket list…'
              />
            </Nav>

            <Nav className='d-flex align-items-center justify-content-center'>
              <h2 className='text-dark p-0 m-0'>
                Bucket List
                {totalCount > 0 && (
                  <span
                    className='badge bg-secondary ms-2'
                    style={{ fontSize: '14px', verticalAlign: 'middle' }}
                  >
                    {totalCount}
                  </span>
                )}
              </h2>
            </Nav>

            <Nav
              className='d-flex align-items-center justify-content-end'
              style={{ gap: '12px' }}
            >
              <Button
                size='md'
                width={167}
                onClick={() => {
                  setEditItem(null);
                  setShowAddModal(true);
                }}
              >
                + Add Idea
              </Button>
            </Nav>
          </Navbar>

          {/* Content */}
          {isLoading ? (
            <div className='text-center py-5 text-muted'>Loading…</div>
          ) : error ? (
            <div className='alert alert-danger'>{error}</div>
          ) : filteredByRegion.length === 0 ? (
            <div className='bl-empty-state'>
              {searchQuery || selectedRegion ? (
                <p>No items match your search.</p>
              ) : (
                <div className='bl-welcome-banner'>
                  {/* <div className='bl-welcome-banner__header'>
                    <h2 className='bl-welcome-banner__title font-libre'>
                      Your Travel Bucket List
                    </h2>
                  </div> */}
                  <div className='bl-welcome-banner__body'>
                    <p className='bl-welcome-banner__intro'>
                      Save travel ideas from anywhere — places you discover,
                      activities you want to try, or destinations you dream
                      about. Your bucket list keeps everything in one place so
                      you can plan when you're ready.
                    </p>
                    <div className='bl-welcome-features'>
                      <div
                        className='bl-welcome-feature'
                        style={{
                          background:
                            'linear-gradient(135deg, #842234 0%, #b03050 100%)'
                        }}
                      >
                        <div className='bl-welcome-feature__icon'>
                          <Globe size={28} />
                        </div>
                        <span className='bl-welcome-feature__text'>
                          <strong>Discover &amp; Save:</strong> Add places from
                          the discovery view, public trips, or manually with a
                          link or free-text idea.
                        </span>
                      </div>
                      <div
                        className='bl-welcome-feature'
                        style={{
                          background:
                            'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'
                        }}
                      >
                        <div className='bl-welcome-feature__icon'>
                          <Star size={28} />
                        </div>
                        <span className='bl-welcome-feature__text'>
                          <strong>Organise by Region:</strong> Items are grouped
                          by region automatically. Switch between vertical and
                          horizontal layouts to browse your ideas.
                        </span>
                      </div>
                      <div
                        className='bl-welcome-feature'
                        style={{
                          background:
                            'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                        }}
                      >
                        <div className='bl-welcome-feature__icon'>
                          <ListChecks size={28} />
                        </div>
                        <span className='bl-welcome-feature__text'>
                          <strong>Add to a Trip:</strong> When you're ready to
                          plan, send any bucket list item straight into one of
                          your trips with one click.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : groupLayout === 'horizontal' ? (
            <div className='bl-regions-horizontal'>
              {filteredByRegion.map(({ region, items }) => (
                <BucketListRegionGroup
                  key={region}
                  region={region}
                  items={items}
                  layout='horizontal'
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                  onAddToTrip={handleAddToTrip}
                />
              ))}
            </div>
          ) : (
            filteredByRegion.map(({ region, items }) => (
              <BucketListRegionGroup
                key={region}
                region={region}
                items={items}
                layout='vertical'
                onEdit={handleEdit}
                onRemove={handleRemove}
                onAddToTrip={handleAddToTrip}
              />
            ))
          )}
        </div>
      </div>

      {/* Add / Edit modal */}
      <AddToBucketListModal
        show={showAddModal}
        handleClose={handleAddModalClose}
        editItem={editItem}
      />

      {/* Add to Trip modal */}
      <Modal
        show={!!addToTripItem}
        onHide={() => setAddToTripItem(null)}
        centered
        backdrop='static'
      >
        <Modal.Header closeButton>
          <Modal.Title>Add to Trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted small mb-3'>
            Adding: <strong>{addToTripItem?.title}</strong>
          </p>
          {tripModalError && (
            <div className='alert alert-danger py-2'>{tripModalError}</div>
          )}
          {tripModalLoading ? (
            <div className='text-center text-muted py-3'>Loading trips…</div>
          ) : (
            <Form.Select
              value={selectedTripId}
              onChange={(e) => setSelectedTripId(e.target.value)}
              className='bl-trip-list'
            >
              <option value=''>Select a trip…</option>
              {trips.map((t) => (
                <option
                  key={t.id}
                  value={t.id}
                >
                  {t.title}
                </option>
              ))}
            </Form.Select>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className='btn btn-secondary'
            onClick={() => setAddToTripItem(null)}
          >
            Cancel
          </button>
          <button
            className='btn btn-primary'
            disabled={!selectedTripId || addingToTrip}
            onClick={handleConfirmAddToTrip}
          >
            {addingToTrip ? 'Adding…' : 'Add to Trip'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const BucketListPageWithProvider: React.FC = () => {
  return (
    <BucketListProvider>
      <BucketListPage />
    </BucketListProvider>
  );
};

export default withAuth(BucketListPageWithProvider);
